// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from "../../context/FirebaseConfig";
import { useTranslation } from 'react-i18next';

import DeliveriesTable from './DeliveriesTable';
import DeliveryDetailsModal from '../DeliveryDetails/DeliveryDetailsModal';
import { getDeliverySettings } from '../../shared_components/utils/FirebaseUtils';
import './DeliveriesTab.scss';

function DeliveriesTab({ supplier, supplierData, activeFilters = {} }) {
  const { labelsets, currentUser } = React.useContext(AuthContext);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deliveriesSettings, setDeliveriesSettings] = useState({});
  const { t } = useTranslation();
  
  // Filter deliveries if material filter is active
  const deliveries = useMemo(() => {
    return supplierData?.deliveries || [];
  }, [supplierData?.deliveries]);
  
  React.useEffect(() => {
    getDeliverySettings(firestore, currentUser.company.company).then((settings) => {
      setDeliveriesSettings(settings);
    });
  }, []);

  const handleSelectDelivery = (deliveryId) => {
    setActiveDelivery(deliveryId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Function to get material display name
  const getMaterialDisplayName = (materialKey) => {
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  };

  return (
    <div className="tab-deliveries animate-fade-in">
      <div className="deliveries-header">
        <h3>
          {t('delivery_history')}
          {activeFilters?.material && activeFilters?.material !== 'all' && (
            <span className="material-filter-badge">
              {getMaterialDisplayName(activeFilters?.material)}
            </span>
          )}
        </h3>
        <p className="deliveries-count">
          {deliveries.length} {deliveries.length === 1 ? t('delivery') : t('deliveries')} {t('deliveries_count')}
        </p>
      </div>

      {deliveries.length > 0 ? (
        <div className="deliveries-container">
          <DeliveriesTable 
            deliveries={deliveries}
            activeDelivery={activeDelivery}
            onSelectDelivery={handleSelectDelivery}
          />
          
          {/* Modal for delivery details */}
          <DeliveryDetailsModal 
            show={showModal}
            onHide={handleCloseModal}
            deliveryId={activeDelivery}
            deliveriesSettings={deliveriesSettings}
          />
        </div>
      ) : (
        <div className="empty-message">
          {activeFilters?.material && activeFilters?.material !== 'all' 
            ? t('no_deliveries_found_for_material', { material: getMaterialDisplayName(activeFilters?.material) })
            : t('no_deliveries_found')
          }
        </div>
      )}
    </div>
  );
}

export default DeliveriesTab; 